/**
 * VersionHistory Component
 *
 * Displays version history for a document with timestamps and version numbers.
 * Allows selecting/viewing previous versions and shows which version is current.
 *
 * Requirements: 2.6, 2.7
 */

import React, { useCallback } from 'react';
import {
  History24Regular,
  Checkmark16Regular,
  ArrowDownload20Regular,
  Clock16Regular,
  Person16Regular,
} from '@fluentui/react-icons';
import type { DocumentVersion } from '../../types';
import styles from './VersionHistory.module.css';

// ============================================================================
// Types
// ============================================================================

export interface VersionHistoryProps {
  /** Array of document versions to display */
  versions: DocumentVersion[];
  /** Current version number of the document */
  currentVersion: number;
  /** Callback when a version is selected for viewing */
  onSelectVersion?: (version: DocumentVersion) => void;
  /** Callback when download is requested for a version */
  onDownloadVersion?: (version: DocumentVersion) => void;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Document name for display context */
  documentName?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats a date string for display with time
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Sorts versions in descending order (newest first)
 */
function sortVersionsDescending(versions: DocumentVersion[]): DocumentVersion[] {
  return [...versions].sort((a, b) => b.version - a.version);
}

// ============================================================================
// Sub-Components
// ============================================================================

interface VersionRowProps {
  version: DocumentVersion;
  isCurrent: boolean;
  onSelect?: (version: DocumentVersion) => void;
  onDownload?: (version: DocumentVersion) => void;
}

/**
 * Individual version row component
 */
const VersionRow: React.FC<VersionRowProps> = ({
  version,
  isCurrent,
  onSelect,
  onDownload,
}) => {
  const handleSelect = useCallback(() => {
    onSelect?.(version);
  }, [version, onSelect]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect?.(version);
      }
    },
    [version, onSelect]
  );

  const handleDownload = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onDownload?.(version);
    },
    [version, onDownload]
  );

  const handleDownloadKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        onDownload?.(version);
      }
    },
    [version, onDownload]
  );

  return (
    <div
      className={`${styles.versionRow} ${isCurrent ? styles.currentVersion : ''}`}
      onClick={onSelect ? handleSelect : undefined}
      onKeyDown={onSelect ? handleKeyDown : undefined}
      tabIndex={onSelect ? 0 : undefined}
      role={onSelect ? 'button' : undefined}
      aria-label={`Version ${version.version}${isCurrent ? ' (current)' : ''}`}
      aria-current={isCurrent ? 'true' : undefined}
    >
      {/* Version Badge */}
      <div className={styles.versionBadgeContainer}>
        <span className={`${styles.versionBadge} ${isCurrent ? styles.currentBadge : ''}`}>
          v{version.version}
        </span>
        {isCurrent && (
          <span className={styles.currentIndicator}>
            <Checkmark16Regular />
            <span className={styles.currentLabel}>Current</span>
          </span>
        )}
      </div>

      {/* Version Details */}
      <div className={styles.versionDetails}>
        <div className={styles.detailRow}>
          <Clock16Regular className={styles.detailIcon} />
          <span className={styles.timestamp}>{formatDateTime(version.uploadedAt)}</span>
        </div>
        <div className={styles.detailRow}>
          <Person16Regular className={styles.detailIcon} />
          <span className={styles.uploadedBy}>{version.uploadedBy}</span>
        </div>
        <span className={styles.fileSize}>{formatFileSize(version.size)}</span>
      </div>

      {/* Actions */}
      {onDownload && (
        <button
          type="button"
          className={styles.downloadButton}
          onClick={handleDownload}
          onKeyDown={handleDownloadKeyDown}
          aria-label={`Download version ${version.version}`}
          title="Download this version"
        >
          <ArrowDownload20Regular />
        </button>
      )}
    </div>
  );
};

// ============================================================================
// Loading Skeleton
// ============================================================================

const LoadingSkeleton: React.FC = () => (
  <div className={styles.skeletonContainer}>
    {[1, 2, 3].map((i) => (
      <div key={i} className={styles.skeletonRow}>
        <div className={`${styles.skeleton} ${styles.skeletonBadge}`} />
        <div className={styles.skeletonDetails}>
          <div className={`${styles.skeleton} ${styles.skeletonTimestamp}`} />
          <div className={`${styles.skeleton} ${styles.skeletonUser}`} />
        </div>
        <div className={`${styles.skeleton} ${styles.skeletonButton}`} />
      </div>
    ))}
  </div>
);

// ============================================================================
// Empty State
// ============================================================================

const EmptyState: React.FC = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <History24Regular />
    </div>
    <p className={styles.emptyText}>No version history available</p>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

/**
 * VersionHistory displays a list of document versions with timestamps and version numbers.
 *
 * Features:
 * - Displays versions in descending order (newest first)
 * - Shows which version is current with visual indicator
 * - Allows selecting/viewing previous versions
 * - Provides download action for each version
 * - Shows loading skeleton while data is being fetched
 * - Shows empty state when no versions are available
 * - Supports keyboard navigation and accessibility
 *
 * @example
 * // Basic usage
 * <VersionHistory
 *   versions={document.versions}
 *   currentVersion={document.currentVersion}
 *   onSelectVersion={(version) => console.log('Selected:', version)}
 *   onDownloadVersion={(version) => handleDownload(version)}
 * />
 *
 * @example
 * // With loading state
 * <VersionHistory
 *   versions={[]}
 *   currentVersion={1}
 *   isLoading={true}
 * />
 */
export const VersionHistory: React.FC<VersionHistoryProps> = ({
  versions,
  currentVersion,
  onSelectVersion,
  onDownloadVersion,
  isLoading = false,
  documentName,
}) => {
  // Sort versions in descending order (newest first)
  const sortedVersions = sortVersionsDescending(versions);

  // Show loading skeleton
  if (isLoading) {
    return (
      <div className={styles.versionHistory}>
        <div className={styles.header}>
          <History24Regular className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Version History</h3>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  // Show empty state if no versions
  if (versions.length === 0) {
    return (
      <div className={styles.versionHistory}>
        <div className={styles.header}>
          <History24Regular className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Version History</h3>
        </div>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className={styles.versionHistory}>
      {/* Header */}
      <div className={styles.header}>
        <History24Regular className={styles.headerIcon} />
        <div className={styles.headerContent}>
          <h3 className={styles.headerTitle}>Version History</h3>
          {documentName && (
            <span className={styles.documentName}>{documentName}</span>
          )}
        </div>
        <span className={styles.versionCount}>
          {versions.length} version{versions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Version List */}
      <div className={styles.versionList} role="list" aria-label="Document versions">
        {sortedVersions.map((version) => (
          <VersionRow
            key={version.version}
            version={version}
            isCurrent={version.version === currentVersion}
            onSelect={onSelectVersion}
            onDownload={onDownloadVersion}
          />
        ))}
      </div>
    </div>
  );
};

export default VersionHistory;
